import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination, TextField, Typography,
} from '@mui/material';
import { AuthService } from '../../../services/auth.service';
import { SupplierService } from '../../../services/supplier.service';
import { Supplier } from '../../../interfaces/procurement/supplier.interface';
import { BillRequest, BillRequestStatus, SupplierBillRequestService } from '../../../services/supplierBillRequest.service';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import SearchableSelect from '../../common/SearchableSelect';
import ScrollableTable from '../../common/ScrollableTable';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { saveBlob } from '../../../utils/saveBlob';
import ConvertBillRequestDialog from './ConvertBillRequestDialog';

// ---- F8: staff inbox of bills that suppliers sent through the supplier portal ----
const STATUSES: BillRequestStatus[] = ['SUBMITTED', 'UNDER_REVIEW', 'CONVERTED', 'REJECTED', 'WITHDRAWN'];
export const BILL_REQUEST_COLOR: Record<BillRequestStatus, 'info' | 'warning' | 'success' | 'error' | 'default'> = {
  SUBMITTED: 'info', UNDER_REVIEW: 'warning', CONVERTED: 'success', REJECTED: 'error', WITHDRAWN: 'default',
};
const OPEN = (s: BillRequestStatus) => s === 'SUBMITTED' || s === 'UNDER_REVIEW';
// Same type-to-extension map as the server's download (a JPG invoice must not save as .pdf).
const EXT: Record<string, string> = { 'application/pdf': '.pdf', 'image/png': '.png', 'image/jpeg': '.jpg' };

export const BillRequestInbox: React.FC = () => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const toast = useToast();
  const navigate = useNavigate();
  const canReview = usePermission(PERMISSIONS.SUPPLIER_PORTAL_MANAGE);
  const canCreateBill = usePermission(PERMISSIONS.SUPPLIER_BILL_CREATE);
  const [status, setStatus] = useState<string>('OPEN');
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [rows, setRows] = useState<BillRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<{ kind: 'comment' | 'reject'; row: BillRequest } | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState<BillRequest | null>(null);

  useEffect(() => {
    SupplierService.listByInstitution(0, 500).then(r => setSuppliers(r.content)).catch(() => setSuppliers([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await SupplierBillRequestService.list(institutionId, { status, supplierId }, page, 50);
      setRows(r.rows); setTotal(r.total);
    } catch (e) {
      toast.showApiError(e, 'Failed to load bill requests');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status, supplierId, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const download = async (r: BillRequest) => {
    try { saveBlob(await SupplierBillRequestService.file(institutionId, r.id), `${r.requestNo}${EXT[r.contentType ?? ''] ?? ''}`); }
    catch (e) { toast.showApiError(e, 'Failed to download the file'); }
  };

  const sendDialog = async () => {
    if (!dialog) return;
    setBusy(true);
    try {
      if (dialog.kind === 'comment') {
        await SupplierBillRequestService.comment(institutionId, dialog.row.id, text.trim());
        toast.showSuccess('Comment saved; the supplier sees it');
      } else {
        await SupplierBillRequestService.reject(institutionId, dialog.row.id, text.trim());
        toast.showSuccess('Bill request rejected');
      }
      setDialog(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Supplier bill requests</Typography>
      <Paper sx={{ p: 2 }} data-help-section="procurement.billReq.table">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="procurement.billReq.filter">
          <TextField data-help="procurement.billReq.filterStatus" select size="small" label="Status" value={status} sx={{ flex: '1 1 200px', maxWidth: 260 }}
            onChange={e => { setStatus(e.target.value); setPage(0); }}>
            <MenuItem value="OPEN">Open (submitted or under review)</MenuItem>
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
          </TextField>
          <Box sx={{ flex: '1 1 240px', maxWidth: 360 }} data-help="procurement.billReq.filterSupplier">
            <SearchableSelect<Supplier> size="small" options={suppliers} value={supplierId} label="Supplier"
              getOptionLabel={s => s.name} onChange={id => { setSupplierId(id); setPage(0); }} />
          </Box>
        </Box>
        {loading ? <CircularProgress /> : (
          <ScrollableTable>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell data-help="procurement.billReq.col.request">Request</TableCell><TableCell data-help="procurement.billReq.col.supplier">Supplier</TableCell><TableCell data-help="procurement.billReq.col.invoice">Invoice</TableCell>
                <TableCell data-help="procurement.billReq.col.date">Invoice date</TableCell><TableCell data-help="procurement.billReq.col.amount" align="right">Amount</TableCell><TableCell data-help="procurement.billReq.col.po">PO</TableCell>
                <TableCell data-help="procurement.billReq.col.status">Status</TableCell><TableCell data-help="procurement.billReq.col.submitted">Submitted</TableCell><TableCell data-help="procurement.billReq.col.actions" />
              </TableRow></TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={9} align="center">No bill requests.</TableCell></TableRow>}
                {rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.requestNo}</TableCell>
                    <TableCell>{r.supplierName ?? '-'}</TableCell>
                    <TableCell>{r.supplierInvoiceNo}{r.reviewComment ? <Typography variant="caption" display="block" color="text.secondary">{r.reviewComment}</Typography> : null}</TableCell>
                    <TableCell>{formatDate(r.invoiceDate)}</TableCell>
                    <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                    <TableCell>{r.poNumber ?? '-'}</TableCell>
                    <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={BILL_REQUEST_COLOR[r.status]} /></TableCell>
                    <TableCell>{r.submittedAt ? formatDateTime(r.submittedAt) : '-'}{r.submittedByName ? ` by ${r.submittedByName}` : ''}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {r.hasFile && <Button data-help="procurement.billReq.action.file" size="small" onClick={() => download(r)}>File</Button>}
                      {canReview && OPEN(r.status) && (
                        <>
                          <Button data-help="procurement.billReq.action.comment" size="small" onClick={() => { setText(r.reviewComment ?? ''); setDialog({ kind: 'comment', row: r }); }}>Comment</Button>
                          <Button data-help="procurement.billReq.action.reject" size="small" color="error" onClick={() => { setText(''); setDialog({ kind: 'reject', row: r }); }}>Reject</Button>
                          {canCreateBill && <Button data-help="procurement.billReq.action.convert" size="small" variant="contained" onClick={() => setConverting(r)}>Convert</Button>}
                        </>
                      )}
                      {r.status === 'CONVERTED' && r.supplierBillId && (
                        <Button data-help="procurement.billReq.action.open" size="small" onClick={() => navigate(`/procurement/supplier-bills/${r.supplierBillId}`)}>Open bill</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        )}
        <TablePagination component="div" count={total} page={page} rowsPerPage={50} rowsPerPageOptions={[50]}
          onPageChange={(_, p) => setPage(p)} />
      </Paper>

      {dialog && (
        <Dialog open onClose={() => !busy && setDialog(null)} maxWidth="sm" fullWidth data-help-page="procurement.billReqDialog.page">
          <DialogTitle>{dialog.kind === 'comment' ? 'Comment to the supplier' : `Reject ${dialog.row.requestNo}`}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {dialog.kind === 'reject' ? 'The supplier sees this reason in the portal.' : 'The supplier sees this comment in the portal.'}
            </Typography>
            <TextField data-help="procurement.billReqDialog.field.text" label={dialog.kind === 'reject' ? 'Reason shown to the supplier' : 'Comment shown to the supplier'} required fullWidth multiline minRows={3}
              helperText="Visible to the supplier. Keep internal notes out of it."
              value={text} inputProps={{ maxLength: 1000 }} onChange={e => setText(e.target.value)} />
          </DialogContent>
          <DialogActions>
            <Button data-help="procurement.billReqDialog.cancel" onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
            <Button data-help="procurement.billReqDialog.save" variant="contained" color={dialog.kind === 'reject' ? 'error' : 'primary'} disabled={busy || !text.trim()} onClick={sendDialog}>
              {dialog.kind === 'reject' ? 'Reject' : 'Save comment'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {converting && (
        <ConvertBillRequestDialog institutionId={institutionId} request={converting} onClose={() => setConverting(null)}
          onConverted={res => {
            setConverting(null);
            toast.showSuccess(res.onApprovalHold ? `Bill ${res.billNumber ?? ''} created and sent for approval` : `Bill ${res.billNumber ?? ''} created`);
            load();
          }} />
      )}
    </Box>
  );
};

export default BillRequestInbox;
// ---- end F8 ----
